import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Badge } from '@atoms/index';
import { ConfirmModal } from '@organisms/ConfirmModal';
import {
  complianceService,
  COURIER_DOCUMENT_TYPES,
  RESTAURANT_DOCUMENT_TYPES,
  type StakeholderDocumentItem,
  type StakeholderEntityType,
} from '@services/complianceService';

interface StakeholderDocumentsModalProps {
  isOpen: boolean;
  entityType: StakeholderEntityType;
  entityId: string;
  entityName: string;
  onClose: () => void;
}

export function StakeholderDocumentsModal({
  isOpen,
  entityType,
  entityId,
  entityName,
  onClose,
}: StakeholderDocumentsModalProps) {
  const documentTypes =
    entityType === 'RESTAURANT' ? RESTAURANT_DOCUMENT_TYPES : COURIER_DOCUMENT_TYPES;

  const [documents, setDocuments] = useState<StakeholderDocumentItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingType, setUploadingType] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    document: StakeholderDocumentItem | null;
  }>({ open: false, document: null });
  const [error, setError] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await complianceService.listStakeholderDocuments(entityType, entityId);
      setDocuments(data);
    } catch {
      setError('Belgeler yüklenemedi');
    } finally {
      setIsLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (isOpen) {
      loadDocuments();
    }
  }, [isOpen, loadDocuments]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const getDocumentForType = (type: string) =>
    documents.find((doc) => doc.documentType === type);

  const handleUpload = async (documentType: string, file: File) => {
    setUploadingType(documentType);
    setError(null);
    try {
      await complianceService.uploadStakeholderDocument(entityType, entityId, documentType, file);
      await loadDocuments();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || 'Belge yüklenemedi';
      setError(message);
    } finally {
      setUploadingType(null);
    }
  };

  const handleDownload = async (documentId: string) => {
    try {
      const url = await complianceService.getStakeholderDocumentDownloadUrl(
        entityType,
        entityId,
        documentId,
      );
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Belge indirilemedi');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.document) return;
    try {
      await complianceService.deleteStakeholderDocument(
        entityType,
        entityId,
        deleteConfirm.document.id,
      );
      await loadDocuments();
    } catch {
      setError('Belge silinemedi');
    } finally {
      setDeleteConfirm({ open: false, document: null });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6">
        <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Kapat" />
        <Card className="relative z-[10000] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-xl font-bold text-gray-900">Resmi Belgeler</h3>
            <p className="text-sm text-gray-500 mt-1">{entityName}</p>
          </div>

          <div className="p-6 overflow-y-auto flex-1 space-y-4">
            {error && (
              <div className="rounded-xl bg-red-50 text-red-700 text-sm px-4 py-3">{error}</div>
            )}

            {isLoading ? (
              <p className="text-gray-500 text-sm">Yükleniyor...</p>
            ) : (
              documentTypes.map((typeDef) => {
                const existing = getDocumentForType(typeDef.value);
                return (
                  <div
                    key={typeDef.value}
                    className="rounded-2xl border border-gray-200 p-4 bg-gray-50/50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-gray-900">{typeDef.label}</h4>
                          {existing ? (
                            <Badge variant="success" size="sm">
                              Yüklü
                            </Badge>
                          ) : (
                            <Badge variant="secondary" size="sm">
                              Eksik
                            </Badge>
                          )}
                        </div>
                        {existing && (
                          <p className="text-xs text-gray-500 mt-1 truncate">
                            {existing.originalFilename} ·{' '}
                            {new Date(existing.updatedAt).toLocaleDateString('tr-TR')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <input
                          ref={(el) => {
                            fileInputRefs.current[typeDef.value] = el;
                          }}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              void handleUpload(typeDef.value, file);
                            }
                            e.target.value = '';
                          }}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          isLoading={uploadingType === typeDef.value}
                          onClick={() => fileInputRefs.current[typeDef.value]?.click()}
                        >
                          {existing ? 'Değiştir' : 'Yükle'}
                        </Button>
                        {existing && (
                          <>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleDownload(existing.id)}
                            >
                              İndir
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() =>
                                setDeleteConfirm({ open: true, document: existing })
                              }
                            >
                              Sil
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="p-4 border-t border-gray-100 flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              Kapat
            </Button>
          </div>
        </Card>
      </div>

      <ConfirmModal
        isOpen={deleteConfirm.open}
        title="Belgeyi Sil"
        description="Bu belge kalıcı olarak silinecek. Devam etmek istiyor musunuz?"
        confirmText="Sil"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm({ open: false, document: null })}
      />
    </>
  );
}
